import { MouseEvent, useEffect, useMemo, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectHasRegions,
  selectHasServiceLocations,
  setCurrentClinic,
  setCurrentRegion
} from "features/auth/auth-slice";
import { useLazyGetClinicQuery } from "services/clinics";
import { ClinicsResponse } from "services/types/clinics.type";
import { getClinicSelectorLabels } from "utils/clinics.util";

type CurrentClinicSelectorProps = {
  isLargeScreen: boolean;
  minimalView: boolean;
  clinics: ClinicsResponse;
  /** CLINIC_SELECTOR_DISPLAY_NAME_FEATURE: list clinics by their own display name when set. */
  showDisplayNames?: boolean;
};

export default function ClinicSelector({
  isLargeScreen,
  minimalView,
  clinics,
  showDisplayNames = false
}: CurrentClinicSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const hasServiceLocations = useAppSelector(selectHasServiceLocations);
  const dispatch = useAppDispatch();
  const [getClinicDetails] = useLazyGetClinicQuery();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget.parentElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const displayNameLabels = useMemo(
    () => (showDisplayNames ? getClinicSelectorLabels(clinics ?? []) : null),
    [showDisplayNames, clinics]
  );
  // The selected clinic is labelled from the list, so the header and its row always agree (the
  // stored clinic is the full-detail fetch, which the list's duplicate check cannot see).
  const largeScreenLabel = (clinic: { id: number; fullName: string }) =>
    displayNameLabels?.get(clinic.id) ?? clinic.fullName;

  useEffect(() => {
    dispatch(setCurrentRegion(null));
  }, [dispatch]);

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {!minimalView && (
          <Typography variant="body1" color="textPrimary" fontWeight="700">
            {isLargeScreen
              ? currentClinic && largeScreenLabel(currentClinic)
              : currentClinic?.name}
          </Typography>
        )}
        {clinics && clinics.length > 1 && (
          <IconButton
            data-testid="clinics-selector"
            onClick={handleClick}
            sx={{ p: 0.25 }}
          >
            <ArrowDropDownIcon />
          </IconButton>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ sx: { minWidth: "20rem", zIndex: 2000 } }}
        sx={{ ml: "3.5rem", zIndex: 2000 }}
      >
        {clinics?.map((clinic) => (
          <MenuItem
            key={clinic.id}
            value={clinic.id}
            selected={currentClinic?.id === clinic.id}
            onClick={() => {
              // First, immediately set the clinic with minimal details
              dispatch(
                setCurrentClinic({
                  currentClinic: { ...clinic },
                  numberOfClinics: clinics.length,
                  hasRegions,
                  hasServiceLocations
                })
              );

              // Then fetch and update with full clinic details
              if (clinic?.id) {
                const selectedClinicId = clinic.id;
                getClinicDetails({ clinicId: selectedClinicId })
                  .then((result) => {
                    if (result.data) {
                      // Only update if this is still the currently selected clinic
                      dispatch((dispatch, getState) => {
                        const currentClinicId =
                          getState().auth.currentClinic?.id;
                        if (currentClinicId === selectedClinicId) {
                          dispatch(
                            setCurrentClinic({
                              currentClinic: result.data,
                              numberOfClinics: clinics.length,
                              hasRegions,
                              hasServiceLocations
                            })
                          );
                        }
                      });
                    }
                  })
                  .catch((error) => {
                    console.error(
                      "Failed to fetch full clinic details:",
                      error
                    );
                  });
              }

              handleClose();
            }}
          >
            {isLargeScreen ? largeScreenLabel(clinic) : clinic.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
